import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
/** Background, typography, and border color classes (Figma Chart panel). */
const SUMMARY_CARD_STYLES = {
    success: {
        background: 'bg-green-50',
        idleBorderColor: 'border-green-200',
        selectedBorderColor: 'border-green-500',
        valueText: 'text-green-700',
        labelText: 'text-gray-900',
    },
    warning: {
        background: 'bg-yellow-50',
        idleBorderColor: 'border-yellow-200',
        selectedBorderColor: 'border-yellow-600',
        valueText: 'text-yellow-700',
        labelText: 'text-gray-900',
    },
    error: {
        background: 'bg-red-50',
        idleBorderColor: 'border-red-200',
        selectedBorderColor: 'border-red-500',
        valueText: 'text-red-500',
        labelText: 'text-gray-900',
    },
};
const SummaryCard = ({ value, label, level, selected, onClick, }) => {
    const palette = SUMMARY_CARD_STYLES[level];
    const borderColorClass = selected ? palette.selectedBorderColor : palette.idleBorderColor;
    const borderPaddingClasses = selected
        ? // Decrease padding when selected to keep the card from shifting due to the wider border.
            'border-2 pl-[23px] pr-[15px] py-[15px]'
        : 'border-1 pl-[24px] pr-[16px] py-[16px]';
    const onKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };
    return (_jsxs("div", { className: `nucleus-flex nucleus-flex-1 nucleus-flex-col nucleus-gap-1 nucleus-rounded-lg nucleus-transition-[border-width] ` +
            `${borderPaddingClasses} border-solid ${borderColorClass} ${palette.background} ` +
            `focus-visible:nucleus-outline-none focus-visible:nucleus-ring-2 focus-visible:nucleus-ring-primary-500 focus-visible:nucleus-ring-offset-2`, onClick: onClick, onKeyDown: onKeyDown, role: 'button', tabIndex: 0, "aria-pressed": selected, children: [_jsx("span", { className: `nucleus-text-sm nucleus-font-semibold ${palette.labelText}`, children: label }), _jsx("span", { className: `text-3xl font-semibold leading-9 ${palette.valueText}`, children: value })] }));
};
export const BulkAssignmentSummary = ({ assignments, environmentSites, documents, unrecognizedSitesCount, unrecognizedIcfDocumentsCount, selectedSummaryCard, onSelectedSummaryCardChange, }) => {
    const translate = useLocalizeMessage();
    const unmappedSitesOrDocumentsCount = countUnmappedSitesAndDocuments(assignments, environmentSites, documents);
    const unrecognizedFromAgentCount = unrecognizedSitesCount + unrecognizedIcfDocumentsCount;
    const handleSummaryCardClick = useCallback((level) => {
        onSelectedSummaryCardChange(selectedSummaryCard === level ? undefined : level);
    }, [onSelectedSummaryCardChange, selectedSummaryCard]);
    return (_jsxs("div", { className: 'nucleus-flex nucleus-flex-wrap nucleus-gap-6 nucleus-w-full', "data-testid": 'bulk-assignment-review-summary-tiles', children: [_jsx(SummaryCard, { value: assignments.length, label: translate('Mappings found'), level: 'success', selected: selectedSummaryCard === 'success', onClick: () => {
                    handleSummaryCardClick('success');
                } }), _jsx(SummaryCard, { value: unmappedSitesOrDocumentsCount, label: translate('Warnings'), level: 'warning', selected: selectedSummaryCard === 'warning', onClick: () => {
                    handleSummaryCardClick('warning');
                } }), _jsx(SummaryCard, { value: unrecognizedFromAgentCount, label: translate('Errors'), level: 'error', selected: selectedSummaryCard === 'error', onClick: () => {
                    handleSummaryCardClick('error');
                } })] }));
};
/** Sites and consent documents in scope that have no row in the current assignment list (agent output + user edits). */
function countUnmappedSitesAndDocuments(assignments, environmentSites, documents) {
    const mappedSiteIds = new Set(assignments.map((a) => a.environmentSiteId));
    const mappedDocumentIds = new Set(assignments.map((a) => a.consentDocumentId));
    const unmappedSites = environmentSites.filter((s) => !mappedSiteIds.has(s.id)).length;
    const unmappedDocuments = documents.filter((d) => !mappedDocumentIds.has(d.id)).length;
    return unmappedSites + unmappedDocuments;
}
